import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { getCurrentUser } from "@/lib/authUser";
import { requestWithAuthRecovery } from "@/lib/authRequest";

let refresherSubscriptionId = 0;

export function useRefresherCount() {
  const [count, setCount] = useState<number>(0);
  const { workspace, selectedGroupId } = useWorkspace();

  useEffect(() => {
    void fetchRefresherCount();

    const userId = getCurrentUser();
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    void userId.then((user) => {
      if (!user || cancelled) return;

      const filterConfig = {
        event: '*' as const,
        schema: 'public',
        table: 'leads',
        filter: workspace === "groups" && selectedGroupId
          ? `group_id=eq.${selectedGroupId}`
          : `user_id=eq.${user.id}`,
      };

      // Every effect instance needs its own topic. Reusing a topic while the
      // previous channel is still leaving can return an already-subscribed
      // channel, and adding another callback then throws synchronously.
      const subscriptionId = ++refresherSubscriptionId;
      channel = supabase
        .channel(`refresher-changes-${workspace}-${selectedGroupId || user.id}-${subscriptionId}`)
        .on('postgres_changes', filterConfig, () => {
          void fetchRefresherCount();
        })
        .subscribe();
    });

    return () => {
      cancelled = true;
      if (channel) {
        void supabase.removeChannel(channel);
      }
    };
  }, [workspace, selectedGroupId]);

  const fetchRefresherCount = async () => {
    try {
      const user = await getCurrentUser();
      if (!user) {
        setCount(0);
        return;
      }

      // Fetch reminder days from appropriate table
      let globalDays = 30;
      
      if (workspace === "groups" && selectedGroupId) {
        const { data: groupData } = await requestWithAuthRecovery(() =>
          supabase
            .from("groups")
            .select("reminder_days")
            .eq("id", selectedGroupId)
            .single(),
        );
        
        globalDays = groupData?.reminder_days || 30;
      } else {
        const { data: profileData } = await requestWithAuthRecovery(() =>
          supabase
            .from("profiles")
            .select("reminder_days")
            .eq("user_id", user.id)
            .single(),
        );
        
        globalDays = profileData?.reminder_days || 30;
      }

      // Fetch all leads (no status filtering to match Refresher tab)
      let query = supabase
        .from("leads")
        .select("*");

      if (workspace === "groups" && selectedGroupId) {
        query = query.eq("group_id", selectedGroupId);
      } else {
        query = query.eq("user_id", user.id).is("group_id", null);
      }

      const { data, error } = await requestWithAuthRecovery(() => query);

      if (error) throw error;

      // Filter leads where timer has hit 0 (matching Refresher tab display logic)
      const filteredData = (data || []).filter(lead => {
        const lastRefreshDate = lead.last_refreshed_at 
          ? new Date(lead.last_refreshed_at) 
          : new Date(lead.created_at);
        
        // Exclude leads manually moved to cold (very old date)
        if (lastRefreshDate < new Date('2000-01-01')) {
          console.log(`[RefresherCount] Excluding ${lead.lead_name}: manually moved to cold`);
          return false;
        }
        
        // Calculate days since last refresh
        const now = new Date();
        const daysSinceRefresh = Math.floor((now.getTime() - lastRefreshDate.getTime()) / (1000 * 60 * 60 * 24));
        
        // Get custom or global reminder days
        const customFields = lead.custom_fields as Record<string, any> | null;
        const customDays = customFields?.custom_reminder_days;
        const daysToUse = customDays || globalDays;
        
        // Calculate timer value (days until reminder)
        const timerValue = daysToUse - daysSinceRefresh;
        
        // Include only leads where timer is 0 or negative
        const timerHitZero = timerValue <= 0;
        console.log(`[RefresherCount] ${lead.lead_name}: daysSince=${daysSinceRefresh}, daysToUse=${daysToUse}, timer=${timerValue}, hitZero=${timerHitZero}`);
        
        return timerHitZero;
      });

      setCount(filteredData.length);
      console.log(`[RefresherCount] Badge count: ${filteredData.length} leads need refreshing`);
    } catch (error) {
      console.error("Error fetching refresher count:", error);
      // Keep the last known badge count during temporary connectivity loss.
    }
  };

  return count;
}
